import { TrackToolService } from '@analytics/events';
import type { OnInit } from '@angular/core';
import { Component, computed, effect, inject, input } from '@angular/core';
import { SolarPanelsWizardRendering } from '@feh/solar-panels-models';
import {
  parsedSolarPanelsWizardToolTrackingConfig,
  selectSolarPanelsExpectedConsumption,
  selectSolarPanelsWizardSubmitState,
  solarPanelsWizardFormSubmitAction,
  // solarPanelsWizardQuestionnaireOpenModalAction,
  type SolarPanelsWizardFormValues,
} from '@feh/solar-panels-store';
import {
  SolarPanelsWizardUiComponent,
  UsageQuestionnaireModalComponent,
  type UsageQuestionnaireData,
} from '@feh/solar-panels-ui';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CalculateCardComponent } from '@legacy/common-ui-utility-components';
import { Store } from '@ngrx/store';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MeteringApiActions } from '@feh/shared-api';

import { initializeSolarPanelsFunnelComponent } from '../solar-panels-helpers';
import { createWizardForm } from './solar-panels-wizard-helpers';

@Component({
  selector: 'wl-solar-panels-wizard-container',
  templateUrl: './solar-panels-wizard-container.component.html',
  standalone: true,
  imports: [CalculateCardComponent, SolarPanelsWizardUiComponent],
})
export class SolarPanelsWizardContainerComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly trackToolService = inject(TrackToolService);
  private readonly consumptionEstimate = this.store.selectSignal(
    selectSolarPanelsExpectedConsumption
  );
  private readonly modalService = inject(NgbModal);

  protected readonly trackToolConfig = computed(() => {
    const toolName = this.rendering().fields.componentName.value;
    return parsedSolarPanelsWizardToolTrackingConfig(toolName);
  });
  protected readonly form = computed(() => createWizardForm(this.rendering()));
  protected readonly submitState = this.store.selectSignal(
    selectSolarPanelsWizardSubmitState
  );
  public readonly rendering = input.required<SolarPanelsWizardRendering>();

  constructor() {
    effect(() => {
      const calculatedConsumption = this.consumptionEstimate();

      if (calculatedConsumption) {
        this.form().controls.consumption.setValue(
          String(calculatedConsumption)
        );
      }
    });
  }

  ngOnInit() {
    this.store.dispatch(
      initializeSolarPanelsFunnelComponent(
        'wizard',
        this.rendering().fields.FunnelSettings
      )
    );
  }

  submit() {
    this.form().markAllAsTouched();

    if (this.form().valid) {
      this.store.dispatch(
        solarPanelsWizardFormSubmitAction({
          formValues: this.form().value as SolarPanelsWizardFormValues,
        })
      );
      this.trackToolService.completeTool(this.trackToolConfig());
    }
  }

  /**
   * Help the user estimate energy consumption.
   */
  async openConsumptionDialog() {
    const modal = this.modalService.open(UsageQuestionnaireModalComponent, {
      centered: true,
      keyboard: true,
    });

    const formValues: UsageQuestionnaireData | null = await modal.result;
    if (formValues) {
      this.store.dispatch(
        MeteringApiActions.getHouseTypeExpectedConsumptionV1.requestAction({
          actionId: 'solar-panels-wizard',
          payload: formValues,
        })
      );
    }
  }
}
